import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateGetNewsletterTemplatesByKeyword } from '../../../../queries';
import {
	invalidateTemplateKeywords,
	useTemplateKeywords,
} from '../../../../queries/Templates/useEmailTemplateKeywords';
import { invalidateAllTemplateSystemQuery } from '../../../../queries/Templates/useGetTemplatesByIndustry';
import { useUpdateEmailTemplateKeywords } from '../../../../queries/Templates/useUpdateEmailTemplateKeywords';
import { charmGray } from '../../../styles/colors';
import { PillMultiSelect } from '../../PillMultiSelect/PillMultiSelect';
import { KeywordIcon } from '../../svgs/icons/KeywordIcon';
import { styleSheet } from './styles';

export interface IProps {
	keywords: string[];
	templateId: string;
	industry: Api.Industry;
	invalidateTemplates: () => void;
	readOnly?: boolean;
}

export const TemplateKeywords: React.FC<IProps> = ({
	industry,
	keywords,
	templateId,
	invalidateTemplates,
	readOnly,
}) => {
	const userSession = useUserSession();

	const keywordsQuery = useTemplateKeywords({
		industry,
	});

	const keywordsMutation = useUpdateEmailTemplateKeywords({
		onSuccess: () => {
			invalidateTemplates();
			invalidateTemplateKeywords({ industry });
		},
	});

	const cardKeywords = React.useMemo(() => keywords || [], [keywords]);

	const handleSelectItem = (selected: string) => {
		if (selected && !cardKeywords.find(keyword => keyword === selected)) {
			const updated = [...cardKeywords.filter(keyword => !!keyword), selected];
			keywordsMutation.mutate({
				keywords: updated,
				templateId,
			});
		}
	};

	const handleRemove = (selected: string) => {
		const updated = cardKeywords.filter(k => k !== selected);

		keywordsMutation.mutate({
			keywords: updated,
			templateId,
		});

		invalidateGetNewsletterTemplatesByKeyword({
			impersonationContext: userSession.impersonationContext,
			industry,
			templateType: Api.TemplateType.HtmlNewsletter,
		});
		invalidateAllTemplateSystemQuery();
	};

	return (
		<div className={css(styleSheet.keywordsContainer)}>
			<PillMultiSelect
				variant='keyword'
				accessory={<KeywordIcon fillColor={charmGray} />}
				addButtonLabel='Add Keyword'
				options={readOnly ? [] : keywordsQuery.data}
				selectedOptions={cardKeywords}
				onEditOptions={readOnly ? undefined : handleSelectItem}
				onRemove={readOnly ? undefined : handleRemove}
				onOptionClick={readOnly ? undefined : handleSelectItem}
				disabled={readOnly || keywordsMutation.isPending || keywordsQuery.isPending}
				readOnly={readOnly}
			/>
		</div>
	);
};
